/**
 * Admin 后台布局组件
 *
 * 左侧侧边栏导航 + 顶部栏（用户名 + 退出）+ 内容区域。
 * PC 端布局，最小宽度 1024px。
 */

import { ReactNode, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import BusinessIcon from '@mui/icons-material/Business'
import CardMembershipIcon from '@mui/icons-material/CardMembership'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import PeopleIcon from '@mui/icons-material/People'
import DescriptionIcon from '@mui/icons-material/Description'
import HistoryIcon from '@mui/icons-material/History'
import DeviceHubIcon from '@mui/icons-material/DeviceHub'
import LogoutIcon from '@mui/icons-material/Logout'
import LockIcon from '@mui/icons-material/Lock'
import MenuIcon from '@mui/icons-material/Menu'
import { useAdminStore } from '@/store/useAdminStore'
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog'
import { VersionChangelogDialog } from '@/components/VersionChangelogDialog'
import { useAppVersion } from '@/hooks/useAppVersion'

const DRAWER_WIDTH = 240

/** 侧边栏菜单项 */
const MENU_ITEMS = [
  { label: '仪表盘', path: '/dashboard', icon: <DashboardIcon /> },
  { label: '品牌管理', path: '/brands', icon: <BusinessIcon /> },
  { label: '员工明细', path: '/employees', icon: <PeopleIcon /> },
  { label: '笔记明细', path: '/notes', icon: <DescriptionIcon /> },
  { label: '额度明细', path: '/quota-logs', icon: <HistoryIcon /> },
  { label: '登录记录', path: '/login-logs', icon: <DeviceHubIcon /> },
  { label: '订阅管理', path: '/subscriptions', icon: <CardMembershipIcon /> },
  { label: '订单管理', path: '/orders', icon: <ReceiptLongIcon /> },
]

interface AdminLayoutProps {
  children: ReactNode
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { username, logout } = useAdminStore()
  const { currentVersion, hasNewVersion, remoteVersion } = useAppVersion()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [pwdOpen, setPwdOpen] = useState(false)
  const [changelogOpen, setChangelogOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    setMobileOpen(false)
  }

  // 当前激活的菜单项
  const currentPath = location.pathname

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo 区域 */}
      <Toolbar sx={{ minHeight: '64px !important', px: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={`${import.meta.env.BASE_URL}logo.svg`}
            alt="研弈引力"
            sx={{ width: 36, height: 36, borderRadius: '8px' }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333', lineHeight: 1.2 }}>
              研弈引力
            </Typography>
            <Typography variant="caption" sx={{ color: '#999', fontSize: '11px' }}>
              服务商后台
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider />

      {/* 导航菜单 */}
      <List sx={{ flex: 1, py: 1 }}>
        {MENU_ITEMS.map((item) => {
          const isActive = currentPath.startsWith(item.path)
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: '8px',
                  backgroundColor: isActive ? '#FFF0E0' : 'transparent',
                  color: isActive ? '#FF8C00' : '#555',
                  '&:hover': {
                    backgroundColor: isActive ? '#FFE8CC' : '#f5f5f5',
                  },
                  py: 1.2,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: isActive ? '#FF8C00' : '#999' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>

      {/* 底部版本信息：点击打开版本迭代日志 */}
      <Box sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
        <Box
          onClick={() => setChangelogOpen(true)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: '6px',
            px: 1,
            py: 0.5,
            transition: 'background-color 0.2s',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
        >
          <Typography variant="caption" sx={{ color: '#999', fontSize: '11px' }}>
            研弈引力 {currentVersion || 'v1.0.0'}
          </Typography>
          {hasNewVersion && remoteVersion && (
            <Chip
              size="small"
              label={`新版 ${remoteVersion}`}
              color="warning"
              sx={{ height: 18, fontSize: '10px', fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', minWidth: '1024px', backgroundColor: '#f5f5f5' }}>
      {/* 侧边栏 */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid #e0e0e0',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* 主内容区 */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* 顶部栏 */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            borderBottom: '1px solid #e0e0e0',
            color: '#333',
          }}
        >
          <Toolbar sx={{ minHeight: '56px !important' }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {username || '管理员'}
              </Typography>
              <Tooltip title="修改密码">
                <IconButton onClick={() => setPwdOpen(true)} size="small" sx={{ color: '#999' }}>
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="退出登录">
                <IconButton onClick={handleLogout} size="small" sx={{ color: '#999' }}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        {/* 页面内容 */}
        <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>{children}</Box>
      </Box>

      {/* 修改密码弹窗 */}
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />

      {/* 版本迭代日志弹窗 */}
      <VersionChangelogDialog
        open={changelogOpen}
        currentVersion={currentVersion || 'v1.0.0'}
        onClose={() => setChangelogOpen(false)}
      />
    </Box>
  )
}

export default AdminLayout
